// Import styles
import './style.scss';

// Import utilities
import {
	getDOMElements
} from '@builder-legacy/common/utilities';

const {
	confirm,
	isDefined,
	isEmpty
} = _uo.utility;

import {
	getConditionsGroupById,
	updateRemoteConditionsMeta
} from '@builder-legacy/item/conditions/utilities';

import { shouldBeSortable } from '@builder-legacy/item/sortability/utilities';
import { getActions } from '@builder-legacy/item/actions/utilities';

import ItemConditionsModalList from '@builder-legacy/item/conditions/modal-list';
// import ConditionPill from '@builder-legacy/item/conditions/condition-pill';

// Load class that creates the grid with integrations
import ItemCreatorIntegrations from '@builder-legacy/item/integrations-grid';

import { sprintf } from '@wordpress/i18n';
import Actions from '@builder-legacy/item/actions';

/**
 * Creates the visual representation of the conditions
 * 
 * @deprecated This class is deprecated and will be removed once the new recipe object is implemented
 */
export default class ConditionsBlock {
	/**
	 * Checks if the the parameter is valid and invokes the method to render the conditions block
	 *
	 * @param {Object} options                   The options
	 * @param {Object} options.conditionsGroupId The conditions group ID
	 * @return {undefined}
	 */
	constructor( { conditionsGroupId } ) {
		// Save conditions group
		this.conditionsGroupID = conditionsGroupId;

		// Check if the conditions group is defined
		if ( isDefined( this.conditionsGroup ) ) {
			// Create and render block
			this.#render();
		}
	}

	/**
	 * Creates and renders the node of the conditions block
	 *
	 * @return {undefined}
	 */
	#render() {
		// Create template
		const $template = document.createElement( 'div' );
		$template.classList.add( 'uap-conditions-block' );

		// Check if the block should be sortable
		if ( shouldBeSortable( { conditionsGroupID: this.conditionsGroupID } ) ) {
			$template.classList.add( 'uap-conditions-block--sortable' );
		}

		// Add the conditions group ID
		$template.setAttribute( 'data-id', this.conditionsGroupID );

		// Save block
		this.$block = $template;

		// Add inner content
		$template.insertAdjacentHTML(
			'beforeEnd',
			`
			<div class="uap-conditions-block-logic">
				<div class="uap-conditions-block-logic-header">
					<div class="uap-conditions-block-logic-header-mode" dir="auto"></div>
					<div class="uap-conditions-block-logic-header-dropdown"></div>
				</div>
				<div class="uap-conditions-block-logic-content"></div>
				<div class="uap-conditions-block-logic-footer">
					<ua-builder-conditions-add-button 
						size="extra-small" 
						condition-group-id="${ this.conditionsGroupID }"
						parent-id="${ this.conditionsGroup.parentID }"
					>
						${ UncannyAutomator._core.i18n.conditions.addBtnLabel }
					</ua-builder-conditions-add-button>
				</div>
			</div>
			<div class="uap-conditions-block-actions">
				<div class="uap-conditions-block-actions-list" data-id="${ this.conditionsGroupID }">
					<div class="uap-conditions-block-items uap-conditions-block-items--instant"></div>
					<div class="uap-conditions-block-items uap-conditions-block-items--delayed"></div>
					<div class="uap-conditions-block-items uap-conditions-block-items--delay-custom"></div>
					<div class="uap-conditions-block-items uap-conditions-block-items--scheduled"></div>
				</div>
				<div class="uap-conditions-block-actions-footer"></div>
			</div>
		`
		);

		// Check if the user can manage the block
		// Otherwise, add a class to the block
		if ( ! UncannyAutomator._site.automator.has_pro ) {
			// Add class
			this.$block.classList.add( 'uap-conditions-block--cannot-manage' );

			// Disable the block
			this.$block.setAttribute( 'disabled', '' );
		}

		// Create the "Any/all" select
		this.#addModeSelect();

		// Create the "..." icon on the top right corner
		this.#addDropdown();

		// Add condition pills
		this.#addConditionPills();

		// Add the handle to drag the block
		this.#maybeAddDragHandler();

		// Add the warning displayed when Pro is not activated
		this.#maybeAddRequiresProWarning();

		// Add warning about delayed and scheduled actions
		this.#maybeAddDelayedActionsWarning();

		// Add "Add another action" button
		this.#addAddActionButton();

		/**
		 * Append to the DOM
		 */

		// Append block before the first action
		this.#insertBlock();

		// Move actions inside the block
		this.#moveActionsInsideBlock();

		// Listen removed actions
		this.#listenRemovedActions();

		/**
		 * Save references to some important elements in the main node
		 */

		this.$block.$actionsWrapper = this.$actionsWrapper;
	}

	/**
	 * Inserts the node of the block in the DOM
	 *
	 * @return {undefined}
	 */
	#insertBlock() {
		// Check if the conditions group has actions
		// And add it above the first one
		if ( this.conditionsGroup.hasActions ) {
			// Get the element of the first actions of the conditions
			const $lowestPriorityAction = getDOMElements( 'item', {
				id: this.conditionsGroup.actionWithLowestPriority,
			} ).item.get( 0 );

			// Insert block before the action
			$lowestPriorityAction.insertAdjacentElement( 'beforeBegin', this.$block );

			// Stop here
			return true;
		}

		// If it doesn't have action, check if it has a custom priority
		if ( ! isEmpty( this.conditionsGroup.priority ) ) {
			// Try to add find the position
			const actions = getActions( { type: 'instant' } );

			// Get the first action that has a HIGHEST priority and add it before that one
			let targetActionID;

			actions.every( ( action ) => {
				// Get the priority
				const actionPriority = action.menuOrder;

				if ( actionPriority > this.conditionsGroup.priority ) {
					// We found the ID, return it
					targetActionID = action.id;
					
					// Stop here
					return false;
				}

				return true;
			} );

			// Check if the ID is defined. If it's not defined, it means this block has the highest priority, so it should go last
			// The code outside of this condition will handle that
			if ( ! isEmpty( targetActionID ) ) {
				// Get the action node
				const $targetAction = getDOMElements( 'item', { id: targetActionID } ).item.get( 0 );

				// If the action is NOT inside a condition block, render the condition block before the action
				if ( ! UncannyAutomator.recipe.items[ targetActionID ].hasConditions ) {
					// Insert block before the action
					$targetAction
						.insertAdjacentElement( 'beforeBegin', this.$block );
				} else {
					// If the action IS inside a conditions block, render this block outside its conditions block
					try {
						$targetAction.closest( '.uap-conditions-block' )
							.insertAdjacentElement( 'beforeBegin', this.$block );
					} catch ( e ) {
						// Output if the developer mode is enabled
						if ( UncannyAutomator._site.has_debug_enabled ) {
							// eslint-disable-next-line no-console
							console.error( e );
							// eslint-disable-next-line no-console
							console.error( `${ this.conditionsGroup.id }: Couldn't set the conditions block in the correct position.` );
						}

						// Insert block before the action
						$targetAction
							.insertAdjacentElement( 'beforeBegin', this.$block );
					}
				}

				// Stop here
				return true;
			}
		}

		// If it doesn't have any actions and the parent ID is the recipe ID, add it to the bottom
		if ( this.conditionsGroup.parentID == UncannyAutomator._recipe.recipe_id ) {
			// If it doesn't have actions, add it at the bottom
			const $instantActionsContainer = getDOMElements( 'itemsContainer', { type: 'action', actionContext: 'instant' } );
		
			// Add it at the bottom
			$instantActionsContainer.insertAdjacentElement( 'beforeEnd', this.$block );	
		} else {
			// Given that it's not in the top level, we'll have to render this inside another block
			// @todo Change logic once the new recipe object is implemented

			// Get the parent block
			const $parentBlock = document.querySelector( `uap-builder-item-loop[loop-id="${ this.conditionsGroup.parentID }"] .uap-builder-item-loop-items--instant` );

			// Check if the parent block is defined
			if ( $parentBlock ) {
				// Add it at the bottom
				$parentBlock.insertAdjacentElement( 'beforeEnd', this.$block );
			}
		}
	}

	/**
	 * Creates the "Any/all" select
	 * Consider that the select won't be added if there is only one condition
	 * The one that contains the "Run if any of the following conditions are met"
	 *
	 * @return {undefined}
	 */
	#addModeSelect() {
		// Render different versions of the header based on the number of conditions
		// Check if there are none or there is only one, render just the text without the select
		if ( this.conditionsGroup.numberOfConditions <= 1 ) {
			// Add content
			this.$logicHeaderModeWrapper.insertAdjacentHTML(
				'beforeEnd',
				`
				${ UncannyAutomator._core.i18n.conditions.runIf }
			`
			);
		} else if ( this.conditionsGroup.numberOfConditions > 1 ) {
			// If there is more than one, render the select
			// Get the current mode
			const mode = this.conditionsGroup.mode;

			// Wrap text inside the curly brackets inside an span
			const runIfText = UncannyAutomator._core.i18n.conditions.runIfAnyFull.replaceAll(
				/\{\{(.*)\}\}/gm,
				( fullMatch, content ) => `<span>${ content }</span>`
			);

			// Add options
			const content = sprintf(
				runIfText,
				`
				<uo-select>
					<uo-select-option
						value="any"
						text="${ UncannyAutomator._core.i18n.conditions.any }"
						${ mode == 'any' ? 'selected' : '' }
					></uo-select-option>

					<uo-select-option
						value="all"
						text="${ UncannyAutomator._core.i18n.conditions.all }"
						${ mode == 'all' ? 'selected' : '' }
					></uo-select-option>
				</uo-select>
			`
			);

			// Add content
			this.$logicHeaderModeWrapper.insertAdjacentHTML( 'beforeEnd', content );

			// Handle events in "any/all" select
			this.#handleLogicOperatorSelect();
		}
	}

	/**
	 * Creates the "..." icon on the top right corner
	 */
	#addDropdown() {
		// Add the dropdown
		this.$logicHeaderDropdownWrapper.innerHTML = `
			<uo-dropdown align="right">
				<uo-icon id="ellipsis-h" slot="button"></uo-icon>

				<ul>
					<li data-action="delete">
						${ UncannyAutomator._core.i18n.delete }
					</li>
				</ul>
			</uo-dropdown>
		`;

		// Listen clicks to the items
		this.$logicHeaderDropdownWrapper
			.querySelector( 'uo-dropdown' )
			.addEventListener( 'automator/dropdown/item-clicked', ( event ) => {
				// Get the action
				// event.detail.item has the Node of the li, the li has a dataset with the action
				const action = event.detail.item.dataset.action;

				// Check if the user is trying to delete the conditions group
				if ( action == 'delete' ) {
					// Set loading animation on the item
					event.detail.item.setAttribute( 'loading', '' );

					// Delete the conditions group
					this.conditionsGroup.destroy();

					// Update data in the database
					updateRemoteConditionsMeta( {
						onSuccess: () => {
							new Actions();
						}
					} );
				}
			} );
	}

	/**
	 * Adds the warning displayed when users don't have Pro activated
	 *
	 * @return {undefined}
	 */
	#maybeAddRequiresProWarning() {
		// Check if the user can't manage conditions
		if ( ! UncannyAutomator._site.automator.has_pro ) {
			// Create the warning title
			// Get the number of actions to get the singular or plural message
			const warningTitle =
				this.conditionsGroup.numberOfActions == 1
					? UncannyAutomator._core.i18n.conditions.actionWontRun
					: UncannyAutomator._core.i18n.conditions.actionsWontRun;

			// Create the warning content
			// Get the number of actions to get the singular or plural message
			const warningContent = this.conditionsGroup.numberOfActions == 1
				? UncannyAutomator._core.i18n.conditions.actionWontRunContent
				: UncannyAutomator._core.i18n.conditions.actionsWontRunContent;

			// Insert warning before the actions
			this.$actionsWrapper.insertAdjacentHTML(
				'beforeBegin',
				`
				<div class="uap-conditions-block-warning">
					<uo-alert 
						type="warning" 
						heading="${ warningTitle }" 
						no-radius
					>
						${ sprintf( warningContent, `<strong>${  UncannyAutomator._core.i18n.uncannyAutomatorPro  }</strong>` ) }
					</uo-alert>
				</div>
			`
			);
		}
	}

	/**
	 * The order of the conditions might be confusing if users have delayed and scheduled actions inside the conditions block
	 * Add a warning
	 */
	#maybeAddDelayedActionsWarning() {
		// If it doesn't have instant actions, there is no point with adding the alert
		if ( ! this.conditionsGroup.hasInstantActions ) {
			return;
		}

		// If it HAS instant actions, but doesn't have delayed or scheduled, then there is also no point
		if ( ! this.conditionsGroup.hasDelayedActions && ! this.conditionsGroup.hasScheduledActions ) {
			return;
		}

		// We have to add a warning to avoid confusing users
		const $notice = document.createElement( 'uo-alert' );
		$notice.classList.add( 'uap-conditions-block-actions-order-notice' );
		$notice.setAttribute( 'size', 'small' );
		$notice.setAttribute( 'type', 'info' );
		$notice.setAttribute( 'no-radius', '' );
		$notice.innerText = UncannyAutomator._core.i18n.conditions.otherActionsMightRunBefore;

		// Add the notice
		// At this point we can assume it has instant actions, so add the notice after that wrapper
		this.$actionsWrappers.instant.insertAdjacentElement( 'afterEnd', $notice );
	}

	/**
	 * Handle events in the "any/all" select
	 *
	 * @return {undefined}
	 */
	#handleLogicOperatorSelect() {
		// Listen changes on the event
		this.$logicOperatorSelect.addEventListener( 'change', () => {
			// Get current value
			const currentValue = this.$logicOperatorSelect.value;

			// Update the conditions group mode
			this.conditionsGroup.mode = currentValue;

			// Update data in the database
			updateRemoteConditionsMeta( {} );
		} );
	}

	/**
	 * Moves the actions inside the block
	 *
	 * @return {undefined}
	 */
	#moveActionsInsideBlock() {
		// Check if it has actions
		if ( ! this.conditionsGroup.hasActions ) {
			return;
		}

		// Get actions
		const actions = {
			instant: getActions( { type: 'instant', includeOnly: this.conditionsGroup.actions } ),
			delayed: getActions( { type: 'delayed', includeOnly: this.conditionsGroup.actions } ),
			delayCustom: getActions( { type: 'delay-custom', includeOnly: this.conditionsGroup.actions } ),
			scheduled: getActions( { type: 'scheduled', includeOnly: this.conditionsGroup.actions } ),
		}

		// Iterate actions inside this conditions group
		Object.entries( actions ).forEach( ( [ actionType, items ] ) => {
			// Get wrapper
			const $wrapper = this.$actionsWrappers[ actionType ];

			// Iterate the items
			items.forEach( ( action ) => {
				// Get the node of the action
				const $actionNode = getDOMElements( 'item', {
					id: action.id,
				} ).item.get( 0 );

				// Move it inside the actions wrapper
				$wrapper.appendChild( $actionNode );
			} );
		} );

		// // Remove empty wrappers
		// Object.values( this.$actionsWrappers ).forEach( ( $wrapper ) => {
		// 	if ( ! $wrapper.querySelector( '.uap-item' ) ) {
		// 		$wrapper.remove();
		// 	}
		// } );
	}

	/**
	 * Adds condition pills
	 *
	 * @return {undefined}
	 */
	#addConditionPills() {
		// Check if there are conditions
		if ( this.conditionsGroup.hasConditions ) {
			// Iterate conditions
			// eslint-disable-next-line no-unused-vars
			Object.entries( this.conditionsGroup.conditions ).forEach( ( [ conditionId, condition ] ) => {
				// Create and add the pill
				this.$conditionPillsWrapper.insertAdjacentHTML( 'beforeEnd', `
					<ua-builder-conditions-pill
						condition-group-id="${ this.conditionsGroupID }"
						condition-id="${ conditionId }"
					></ua-builder-conditions-pill>
				` );
			} );
		} else {
			// Add "No conditions" message
			this.$conditionPillsWrapper.insertAdjacentHTML(
				'beforeEnd',
				`
				<span class="uap-conditions-block-logic-content-empty">
					${ UncannyAutomator._core.i18n.conditions.noConditions }
				</span>
			`
			);
		}
	}

	/**
	 * Adds a handle for the sortable library
	 *
	 * @return {undefined}
	 */
	#maybeAddDragHandler() {
		// Check if this should be draggable
		if ( shouldBeSortable( { conditionsGroupID: this.conditionsGroupID } ) ) {
			// Create handler
			const $handler = document.createElement( 'div' );
			$handler.classList.add( 'uap-item-drag-handle' );

			// Add icon
			$handler.innerHTML = `<uo-icon id="bars-regular"></uo-icon>`;

			// Add it to the block
			this.$block.insertAdjacentElement( 'afterBegin', $handler );
		}
	}

	/**
	 * Add button to add actions inside the conditions block
	 *
	 * @return {undefined}
	 */
	#addAddActionButton() {
		// Create button
		this.$actionsFooterWrapper.insertAdjacentHTML( 'beforeEnd', `
			<uo-button
				color="secondary"
				size="small"
			>
				${ UncannyAutomator._core.i18n.action.addAnother }
			</uo-button>
		` );

		// Get button
		const $addButton = this.$actionsFooterWrapper.querySelector( 'uo-button' );

		$addButton.addEventListener( 'click', () => {
			new ItemCreatorIntegrations( 'action', null, {
				conditionsGroupID: this.conditionsGroupID,
				parentID: this.conditionsGroup.parentID
			} );
		} );
	}

	/**
	 * Listens when actions are removed and asks the user if they want to remove the conditions block
	 */
	#listenRemovedActions() {
		this.$block.addEventListener( 'automator/conditions-group/remove-action', ( event ) => {
			// Check if the conditions block is empty
			if ( this.conditionsGroup.hasActions ) {
				return;
			}
			
			// Ask the user if they want to remove the conditions block
			confirm( {
				closeOnConfirm: false,
				
				heading: UncannyAutomator._core.i18n.conditions.wantToRemoveConditionsGroup.heading,
				content: '',
				cancelButtonLabel: UncannyAutomator._core.i18n.conditions.wantToRemoveConditionsGroup.cancelButtonLabel,
				confirmButtonLabel: UncannyAutomator._core.i18n.conditions.wantToRemoveConditionsGroup.confirmButtonLabel
			} ).then( ( $dialog ) => {
				// Get the dialog
				$dialog = $dialog.value;

				// Get confirm button
				const $confirmButton = $dialog.querySelector( '.uap-confirm-btn-primary' ); 

				// Start loading animation
				$confirmButton.setAttribute( 'loading', '' );

				// Delete the conditions group
				this.conditionsGroup.destroy();

				// Update data in the database
				updateRemoteConditionsMeta( {
					onSuccess: () => {
						// Close dialog
						$dialog.close();

						new Actions();
					}
				} );
			} );
		} );
	}

	/**
	 * Returns the conditions group
	 * 
	 * @return {ConditionsGroup} The conditions group of this conditions block
	 */
	get conditionsGroup() {
		return getConditionsGroupById( {
			conditionsGroupId: this.conditionsGroupID,
		} );
	}

	/**
	 * Returns the node of the button used to add conditions
	 *
	 * @return {Node} The button
	 */
	get $addConditionButton() {
		return this.$block.querySelector( '.uap-conditions-block-logic-add-condition-btn' );
	}

	/**
	 * Returns the wrapper of the condition pills
	 *
	 * @return {Node} The wrapper
	 */
	get $conditionPillsWrapper() {
		return this.$block.querySelector( '.uap-conditions-block-logic-content' );
	}

	/**
	 * Returns the header of the logic wrapper
	 *
	 * @return {Node} The header
	 */
	get $logicHeader() {
		return this.$block.querySelector( '.uap-conditions-block-logic-header' );
	}

	/**
	 * Returns the wrapper for the Mode select
	 *
	 * @return {Node} The wrapper
	 */
	get $logicHeaderModeWrapper() {
		return this.$block.querySelector( '.uap-conditions-block-logic-header-mode' );
	}

	/**
	 * Returns the wrapper for the "..." icon
	 *
	 * @return {Node} The wrapper
	 */
	get $logicHeaderDropdownWrapper() {
		return this.$block.querySelector( '.uap-conditions-block-logic-header-dropdown' );
	}

	/**
	 * Returns the node of the "any/all" select
	 *
	 * @return {Node} The select
	 */
	get $logicOperatorSelect() {
		return this.$block.querySelector( '.uap-conditions-block-logic-header uo-select' );
	}

	/**
	 * Returns the footer of the logic wrapper
	 *
	 * @return {Node} The footer
	 */
	get $logicFooter() {
		return this.$block.querySelector( '.uap-conditions-block-logic-footer' );
	}

	/**
	 * Returns the node of the wrapper that contains the actions inside a block
	 *
	 * @return {Node} The wrapper
	 */
	get $actionsWrapper() {
		return this.$block.querySelector( '.uap-conditions-block-actions-list' );
	}

	/**
	 * Returns the wrappers to add the actions
	 */
	get $actionsWrappers() {
		return {
			instant: this.$actionsInstantWrapper,
			delayed: this.$actionsDelayedWrapper,
			delayCustom: this.$actionsDelayCustomWrapper,
			scheduled: this.$actionsScheduledWrapper
		}
	}

	/**
	 * Returns the Node of the wrapper of the instant actions
	 *
	 * @return {Node} The wrapper
	 */
	get $actionsInstantWrapper() {
		return this.$block.querySelector( '.uap-conditions-block-items--instant' );
	}

	/**
	 * Returns the Node of the wrapper of the delayed actions
	 *
	 * @return {Node} The wrapper
	 */
	 get $actionsDelayedWrapper() {
		return this.$block.querySelector( '.uap-conditions-block-items--delayed' );
	}

	/**
	 * Returns the Node of the wrapper of the delay actions using custom tokens
	 *
	 * @return {Node} The wrapper
	 */
	get $actionsDelayCustomWrapper() {
		return this.$block.querySelector( '.uap-conditions-block-items--delay-custom' );
	}

	/**
	 * Returns the Node of the wrapper of the scheduled actions
	 *
	 * @return {Node} The wrapper
	 */
	 get $actionsScheduledWrapper() {
		return this.$block.querySelector( '.uap-conditions-block-items--scheduled' );
	}

	/**
	 * Returns the node of the wrapper that contains the actions related to the actions
	 *
	 * @return {Node} The wrapper
	 */
	 get $actionsFooterWrapper() {
		return this.$block.querySelector( '.uap-conditions-block-actions-footer' );
	}
}
